import { Emoji, Message, Reaction } from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { uniq } from 'lodash'
import React from 'react'
import { Platform, View, useWindowDimensions } from 'react-native'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { WEB_SCREEN_WIDTH } from 'shared/constants'

const WIDTH_BALANCE_VALUE = 65

interface Props {
  message: Message | undefined
  reactions: Reaction[]
  position: 'left' | 'right'
  onShowReactors(messageId: string, reaction: Emoji): void
  onReactMessageAction(messageId: String, emoji: Emoji): void
}

export default function Reactions({
  message,
  reactions,
  position,
  onShowReactors,
  onReactMessageAction,
}: Props) {
  const dimensions = useWindowDimensions()
  let width = dimensions.width
  if (Platform.OS === 'web') {
    width =
      WEB_SCREEN_WIDTH < dimensions.width
        ? WEB_SCREEN_WIDTH
        : dimensions.width - 100
  }
  const styles = useStyles({ position, width })

  const emojis = uniq(reactions.map(reaction => reaction.emoji))

  if (!emojis || (emojis && emojis.length === 0)) {
    return null
  }

  function getReactionCount(emoji: Emoji) {
    return reactions.reduce((sum, reaction) => {
      return sum + (reaction.emoji === emoji ? 1 : 0)
    }, 0)
  }

  return (
    <View style={styles.container}>
      {emojis.map((emoji, index) => {
        return (
          <View key={index}>
            <Emojis
              reaction={emoji}
              count={getReactionCount(emoji)}
              position={position}
              messageId={message?.id}
              onShowReactors={onShowReactors}
              onReactMessageAction={onReactMessageAction}
              width={width}
            />
          </View>
        )
      })}
    </View>
  )
}

interface EmojiProps {
  reaction: Emoji
  count: number | undefined
  position: 'left' | 'right'
  messageId: string | undefined
  onShowReactors(messageId: string, reaction: Emoji): void
  onReactMessageAction(messageId: String, emoji: Emoji): void
  width: number
}
function Emojis({
  reaction,
  count,
  position,
  messageId,
  onShowReactors,
  onReactMessageAction,
  width,
}: EmojiProps) {
  const styles = useStyles({ position, width })

  if (!count || !messageId) {
    return null
  }

  return (
    <PressableOpacity
      onPress={() => onReactMessageAction(messageId, reaction)}
      onLongPress={() => onShowReactors(messageId, reaction)}
    >
      <View style={styles.emoji}>
        <Text style={styles.text}>{reaction}</Text>
        <Text style={styles.count}>{count}</Text>
      </View>
    </PressableOpacity>
  )
}

interface StyleProps {
  position: 'left' | 'right'
  width: number
}

const useStyles = makeStyles((theme, props: StyleProps) => ({
  container: {
    flex: 1,
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: props.position === 'right' ? 'flex-end' : 'flex-start',
    alignSelf: props.position === 'right' ? 'flex-end' : 'flex-start',
    margin: 2,
    maxWidth:
      props.position === 'left'
        ? props.width - WIDTH_BALANCE_VALUE
        : props.width,
  },
  emoji: {
    marginLeft: 4,
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor:
      props.position === 'left'
        ? theme.mode === 'dark'
          ? theme.colors.grey10
          : theme.colors.grey6
        : theme.colors.yellow,
    borderRadius: 300,
    padding: 3,
    paddingBottom: 3,
    paddingLeft: 5,
    marginTop: 3,
  },
  text: {
    fontSize: 13,
    marginLeft: 2,
    marginBottom: 0,
    lineHeight: 20,
    color: props.position === 'left' ? theme.colors.black : 'black',
  },
  count: {
    fontSize: 10,
    marginLeft: 4,
    marginRight: 4,
    color: props.position === 'left' ? theme.colors.black : 'black',
  },
}))
